import { Form } from 'react-bootstrap'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { IoMdCloseCircle } from 'react-icons/io'
import styled from 'styled-components/macro'

interface IRadioInput<T extends FieldValues> {
  options: { label: string; value: any }[]

  formControl: Control<T, any>
  fieldName: Path<T>
  horizontal?: boolean
}

export function RadioInput<T extends FieldValues>(props: IRadioInput<T>) {
  const { options, fieldName, formControl } = props
  const {
    field: { ref, onChange, value, onBlur },
    fieldState: { error },
  } = useController({ name: fieldName, control: formControl })

  return (
    <>
      <SelectRadioDiv horizontal={props.horizontal}>
        {options.map((opt, index) => {
          return (
            <RadioData
              onChange={() => onChange(opt.value)}
              onBlur={onBlur}
              ref={ref}
              id={fieldName + index}
              key={fieldName + index}
              checked={value === opt.value}
              className={error?.message && 'text-danger'}
              label={opt.label}
              type="radio"
            />
          )
        })}
      </SelectRadioDiv>

      {error?.message && (
        <ErrorMessage>
          <ErrorIcon /> {error?.message}
        </ErrorMessage>
      )}
    </>
  )
}

const SelectRadioDiv = styled.div<{
  horizontal?: boolean
}>`
  display: flex;
  flex-direction: ${({ horizontal }) => (horizontal ? 'row' : 'column')};
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const RadioData = styled(Form.Check)`
  font-size: 14px;
  display: flex;
  align-items: center;

  &:last-child {
    margin-right: 0px;
  }

  /* Custom radio button styling */
  .form-check-input[type='radio'] {
    width: 16px;
    height: 16px;
    border: 1px solid #c4c4c4; /* Normal border - new-company-gray-inactive */
    background-color: white;
    cursor: pointer;
    box-shadow: none !important; /* Remove any default shadows */
    outline: none !important;
    margin-top: 0; /* Remove default margin */
    margin-right: 8px; /* Spacing between circle and label */
    flex-shrink: 0; /* Prevent radio from shrinking */

    /* Remove default focus ring */
    &:focus {
      border-color: #087443; /* Hover/focus border - new-company-green-active */
      box-shadow: none !important;
    }

    /* Hover state */
    &:hover {
      border-color: #087443; /* new-company-green-active */
    }

    /* Selected state */
    &:checked {
      background-color: white;
      border-color: #087443; /* new-company-green-active */
      background-image: none; /* Remove default checkmark */
      position: relative;

      /* Inner circle */
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: #087443; /* new-company-green-active */
      }
    }

    /* Remove Bootstrap default checked styles */
    &:checked:focus {
      border-color: #087443;
      box-shadow: none !important;
    }

    /* Mobile styles */
    @media (max-width: 1023px) {
      /* Hover state - mobile */
      &:hover {
        border-color: #06512f; /* new-company-green-dark */
      }

      /* Focus state - mobile */
      &:focus {
        border-color: #06512f;
      }

      /* Selected state - mobile */
      &:checked {
        border-color: #06512f; /* new-company-green-dark */

        /* Inner circle - mobile */
        &::before {
          background-color: #06512f; /* new-company-green-dark */
        }
      }

      /* Selected + focus - mobile */
      &:checked:focus {
        border-color: #06512f;
      }
    }
  }
`
export const ErrorMessage = styled.span`
  color: var(--bs-gray-600);
  font-size: 12px;
  display: flex;
  align-items: center;
  margin-top: 2px;
`
export const ErrorIcon = styled(IoMdCloseCircle)`
  color: #c73128;
  font-size: 12px;
  margin-right: 5px;
`
